import { useState, useEffect } from 'react';
import { supabase, safeRefreshSession } from './supabase';
import { supabaseData } from './supabaseData';
import {
  FileText, DollarSign, Calendar, Download, Filter,
  TrendingUp, Users, CreditCard, Clock
} from 'lucide-react';
import { isOfflineModeEnabled, getCachedData } from './offlineCache';
import FinancialDashboard from './financial-dashboard';
import { useTheme } from './ThemeContext';

export default function ReportingFinance({ companyId }) {
  const { colors } = useTheme();
  const [activeTab, setActiveTab] = useState('reporting'); // 'reporting' or 'finance'
  const [loading, setLoading] = useState(true);
  
  // Reporting data
  const [jobs, setJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });
  const [statusFilter, setStatusFilter] = useState('all');

  // Finance data
  const [financialStats, setFinancialStats] = useState({
    totalRevenue: 0,
    completedJobs: 0,
    averageJobValue: 0,
    pendingInvoices: 0
  });

  useEffect(() => {
    if (companyId) {
      loadData();
    } else {
      setLoading(false);
    }
  }, [companyId, dateRange]);

  useEffect(() => {
    filterJobs();
  }, [jobs, statusFilter]);

  const loadData = async (retryCount = 0) => {
    try {
      // Offline: filter cached jobs by date range
      if (!navigator.onLine && isOfflineModeEnabled()) {
        const cachedJobs = getCachedData('jobs', companyId);
        if (cachedJobs) {
          const filtered = cachedJobs.filter(j => {
            const created = j.created_at;
            return created >= dateRange.start && created <= dateRange.end + 'T23:59:59';
          });
          setJobs(filtered);
          const completed = filtered.filter(j => j.status === 'completed');
          const totalRevenue = completed.reduce((sum, j) => sum + (j.quoted_value || 0), 0);
          setFinancialStats({
            totalRevenue,
            completedJobs: completed.length,
            averageJobValue: completed.length > 0 ? totalRevenue / completed.length : 0,
            pendingInvoices: filtered.filter(j => j.status === 'completed').length
          });
          setLoading(false);
          return;
        }
      }

      setLoading(true);

      // Load all jobs with customer info
      const { data: jobsData, error } = await supabaseData
        .from('jobs')
        .select(`
          *,
          customers (
            first_name,
            last_name,
            name
          )
        `)
        .eq('company_id', companyId)
        .gte('created_at', dateRange.start)
        .lte('created_at', dateRange.end + 'T23:59:59')
        .order('created_at', { ascending: false });

      if (error) {
        if (retryCount < 1) {
          console.warn('ReportingFinance: query error, retrying with session refresh...', error.message);
          await safeRefreshSession();
          await new Promise(r => setTimeout(r, 1000));
          return loadData(retryCount + 1);
        }
        throw error;
      }

      setJobs(jobsData || []);

      // Calculate financial stats
      const completed = jobsData?.filter(j => j.status === 'completed') || [];
      const totalRevenue = completed.reduce((sum, j) => sum + (j.quoted_value || 0), 0);
      
      setFinancialStats({
        totalRevenue,
        completedJobs: completed.length,
        averageJobValue: completed.length > 0 ? totalRevenue / completed.length : 0,
        pendingInvoices: jobsData?.filter(j => j.status === 'completed').length || 0
      });

    } catch (error) {
      // Fallback to cache
      if (isOfflineModeEnabled()) {
        const cachedJobs = getCachedData('jobs', companyId);
        if (cachedJobs) {
          const filtered = cachedJobs.filter(j => {
            const created = j.created_at;
            return created >= dateRange.start && created <= dateRange.end + 'T23:59:59';
          });
          setJobs(filtered);
          const completed = filtered.filter(j => j.status === 'completed');
          const totalRevenue = completed.reduce((sum, j) => sum + (j.quoted_value || 0), 0);
          setFinancialStats({
            totalRevenue,
            completedJobs: completed.length,
            averageJobValue: completed.length > 0 ? totalRevenue / completed.length : 0,
            pendingInvoices: filtered.filter(j => j.status === 'completed').length
          });
          setLoading(false);
          return;
        }
      }
      alert('Error loading data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const filterJobs = () => {
    if (statusFilter === 'all') {
      setFilteredJobs(jobs);
    } else {
      setFilteredJobs(jobs.filter(j => j.status === statusFilter));
    }
  };

  const getCustomerName = (customer) => {
    if (!customer) return 'Unknown';
    if (customer.first_name && customer.last_name) {
      return `${customer.last_name}, ${customer.first_name}`;
    }
    return customer.name || 'Unknown';
  };

  const exportToCSV = () => {
    const headers = ['Date', 'Job Number', 'Customer', 'Type', 'Status', 'Amount'];
    const rows = filteredJobs.map(job => [
      new Date(job.created_at).toLocaleDateString(),
      job.job_number,
      getCustomerName(job.customers),
      (job.job_type ? job.job_type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : 'N/A'),
      job.status,
      `$${(job.quoted_value || 0).toFixed(2)}`
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `jobs-report-${dateRange.start}-to-${dateRange.end}.csv`;
    a.click();
  };

  const getStatusColor = (status) => {
    const statusColors = {
      scheduled: '#3498db',
      'in-progress': '#f39c12',
      completed: '#4ecca3',
      cancelled: '#e74c3c'
    };
    return statusColors[status] || '#888';
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: colors.textMuted }}>
        Loading...
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ fontSize: '2rem', margin: '0 0 0.5rem 0', color: colors.text }}>
          📊 Reporting & Finance
        </h1>
        <p style={{ margin: 0, color: colors.textMuted }}>
          Track your business performance and financial metrics
        </p>
      </div>

      {/* Tab Bar - Full Width */}
      <div style={{
        background: colors.bgCard,
        borderRadius: '12px 12px 0 0',
        borderBottom: `2px solid ${colors.border}`,
        marginBottom: 0
      }}>
        <div style={{ display: 'flex', gap: 0 }}>
          <button
            onClick={() => setActiveTab('reporting')}
            style={{
              padding: '1rem 2rem',
              background: activeTab === 'reporting' ? colors.bgInput : 'transparent',
              border: 'none',
              borderBottom: activeTab === 'reporting' ? `3px solid ${colors.accent}` : '3px solid transparent',
              color: activeTab === 'reporting' ? colors.text : colors.textMuted,
              fontSize: '1rem',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              transition: 'all 0.2s'
            }}
          >
            <FileText size={20} />
            Reporting
          </button>
          <button
            onClick={() => setActiveTab('finance')}
            style={{
              padding: '1rem 2rem',
              background: activeTab === 'finance' ? colors.bgInput : 'transparent',
              border: 'none',
              borderBottom: activeTab === 'finance' ? `3px solid ${colors.accent}` : '3px solid transparent',
              color: activeTab === 'finance' ? colors.text : colors.textMuted,
              fontSize: '1rem',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              transition: 'all 0.2s'
            }}
          >
            <DollarSign size={20} />
            Finance
          </button>
        </div>
      </div>

      {/* Content Area */}
      <div style={{
        background: colors.bgCard,
        borderRadius: '0 0 12px 12px',
        padding: '2rem',
        border: `1px solid ${colors.border}`,
        borderTop: 'none'
      }}>
        {activeTab === 'reporting' && (
          <>
            {/* Filters */}
            <div style={{
              display: 'flex',
              gap: '1rem',
              marginBottom: '2rem',
              flexWrap: 'wrap',
              alignItems: 'end'
            }}>
              <div style={{ flex: 1, minWidth: '200px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  color: colors.text,
                  fontSize: '0.9rem',
                  fontWeight: 'bold'
                }}>
                  Start Date
                </label>
                <input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.text,
                    fontSize: '1rem'
                  }}
                />
              </div>
              <div style={{ flex: 1, minWidth: '200px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  color: colors.text,
                  fontSize: '0.9rem',
                  fontWeight: 'bold'
                }}>
                  End Date
                </label>
                <input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.text,
                    fontSize: '1rem'
                  }}
                />
              </div>
              <div style={{ flex: 1, minWidth: '200px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  color: colors.text,
                  fontSize: '0.9rem',
                  fontWeight: 'bold'
                }}>
                  Status
                </label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.text,
                    fontSize: '1rem'
                  }}
                >
                  <option value="all">All Statuses</option>
                  <option value="scheduled">Scheduled</option>
                  <option value="in-progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <button
                onClick={exportToCSV}
                disabled={filteredJobs.length === 0}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: filteredJobs.length === 0 ? colors.bgHover : '#4ecca3',
                  color: filteredJobs.length === 0 ? colors.textMuted : '#000',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  cursor: filteredJobs.length === 0 ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                <Download size={20} />
                Export CSV
              </button>
            </div>

            {/* Jobs Table */}
            <div style={{
              background: colors.bg,
              borderRadius: '8px',
              overflowX: 'auto',
              overflowY: 'hidden'
            }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse'
              }}>
                <thead>
                  <tr style={{ background: colors.bgInput }}>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'left',
                      color: colors.text,
                      fontWeight: 'bold',
                      fontSize: '0.9rem'
                    }}>Date</th>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'left',
                      color: colors.text,
                      fontWeight: 'bold',
                      fontSize: '0.9rem'
                    }}>Job #</th>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'left',
                      color: colors.text,
                      fontWeight: 'bold',
                      fontSize: '0.9rem'
                    }}>Customer</th>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'left',
                      color: colors.text,
                      fontWeight: 'bold',
                      fontSize: '0.9rem'
                    }}>Type</th>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'left',
                      color: colors.text,
                      fontWeight: 'bold',
                      fontSize: '0.9rem'
                    }}>Status</th>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'right',
                      color: colors.text,
                      fontWeight: 'bold',
                      fontSize: '0.9rem'
                    }}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredJobs.length === 0 ? (
                    <tr>
                      <td colSpan="6" style={{
                        padding: '3rem',
                        textAlign: 'center',
                        color: colors.textMuted
                      }}>
                        No jobs found for selected filters
                      </td>
                    </tr>
                  ) : (
                    filteredJobs.map((job, i) => (
                      <tr key={job.id} style={{
                        borderBottom: '1px solid #2a2a2a',
                        background: i % 2 === 0 ? colors.bg : colors.bgCard
                      }}>
                        <td style={{ padding: '1rem', color: colors.textMuted, fontSize: '0.9rem' }}>
                          {new Date(job.created_at).toLocaleDateString()}
                        </td>
                        <td style={{ padding: '1rem', color: colors.text, fontSize: '0.9rem', fontFamily: 'monospace' }}>
                          {job.job_number}
                        </td>
                        <td style={{ padding: '1rem', color: colors.text, fontSize: '0.9rem' }}>
                          {getCustomerName(job.customers)}
                        </td>
                        <td style={{ padding: '1rem', color: colors.textMuted, fontSize: '0.9rem' }}>
                          {(job.job_type ? job.job_type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : 'N/A')}
                        </td>
                        <td style={{ padding: '1rem' }}>
                          <span style={{
                            padding: '0.25rem 0.75rem',
                            background: `${getStatusColor(job.status)}20`,
                            color: getStatusColor(job.status),
                            borderRadius: '6px',
                            fontSize: '0.85rem',
                            fontWeight: 'bold',
                            textTransform: 'capitalize'
                          }}>
                            {job.status?.replace('-', ' ')}
                          </span>
                        </td>
                        <td style={{
                          padding: '1rem',
                          textAlign: 'right',
                          color: '#4ecca3',
                          fontSize: '1rem',
                          fontWeight: 'bold'
                        }}>
                          ${(job.quoted_value || 0).toLocaleString()}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Summary */}
            {filteredJobs.length > 0 && (
              <div style={{
                marginTop: '2rem',
                padding: '1.5rem',
                background: colors.bgInput,
                borderRadius: '8px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <div style={{ color: colors.textMuted, fontSize: '0.9rem', marginBottom: '0.25rem' }}>
                    Total Jobs
                  </div>
                  <div style={{ color: colors.text, fontSize: '1.5rem', fontWeight: 'bold' }}>
                    {filteredJobs.length}
                  </div>
                </div>
                <div>
                  <div style={{ color: colors.textMuted, fontSize: '0.9rem', marginBottom: '0.25rem' }}>
                    Total Revenue
                  </div>
                  <div style={{ color: '#4ecca3', fontSize: '1.5rem', fontWeight: 'bold' }}>
                    ${filteredJobs.reduce((sum, j) => sum + (j.quoted_value || 0), 0).toLocaleString()}
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {activeTab === 'finance' && (
          <FinancialDashboard companyId={companyId} />
        )}
      </div>
    </div>
  );
}
